import React, { useState, useMemo, useRef, useEffect } from 'react'
import { TextField } from 'material-ui'
import SearchIcon from '@material-ui/icons/Search'
import { Chip, List, ListItem, Paper, ListItemText } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { styles } from './styles'
import UnshelteredIcon from '../../icons/UnshelteredIcon'
import ShelteredIcon from '../../icons/ShelteredIcon'
import EventsIcon from '../../icons/EventsIcon'
import { getLocationIcons } from '../../utils'
import TooltipModal from '../../../../../Components/TooltipModal'

const StepLocation = ({
  classes,
  preparedMissionsDatesData = [],
  loading,
  postcodesWithLocations,
  isDistributionZoneExists,
  selectedPostcodes,
  whitelistedLocations,
  setSelectedPostcodes,
  setWhitelistedLocations,
  locations,
}) => {
  const [postcodeSearch, setPostcodeSearch] = useState('')
  const [showPostcodeResults, setShowPostcodeResults] = useState(false)

  const [showLocationResults, setShowLocationResults] = useState(false)
  const [locationSearch, setLocationSearch] = useState('')

  const locationSearchRef = useRef(null)
  const postcodeSearchRef = useRef(null)

  const totalUniqueMissions = useMemo(() => {
    return preparedMissionsDatesData.reduce(
      (acc, item) =>
        acc +
        item.morningMissions +
        item.lunchMissions +
        item.afternoonMissions,
      0,
    )
  }, [preparedMissionsDatesData])

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        postcodeSearchRef.current &&
        !postcodeSearchRef.current.contains(event.target)
      ) {
        setShowPostcodeResults(false)
      }
      if (
        locationSearchRef.current &&
        !locationSearchRef.current.contains(event.target)
      ) {
        setShowLocationResults(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handlePostcodeSearch = e => {
    const value = e.target.value
    setPostcodeSearch(value)
  }
  const handlePostcodeInputFocus = () => {
    setShowPostcodeResults(true)
  }

  const handlePostcodeSelect = item => {
    if (
      !selectedPostcodes.some(
        selectedPostcode => selectedPostcode === item.postcode,
      )
    ) {
      setSelectedPostcodes([...selectedPostcodes, item])
    }
    setPostcodeSearch('')
    setShowPostcodeResults(false)
  }

  const handleRemovePostcode = deletedItem => {
    setSelectedPostcodes(
      selectedPostcodes.filter(item => item.postcode !== deletedItem.postcode),
    )
  }

  const filteredPostcodeResults = useMemo(
    () =>
      postcodesWithLocations.filter(
        item =>
          item.postcode.includes(postcodeSearch) &&
          !selectedPostcodes.some(
            selectedPostcode => selectedPostcode.postcode === item.postcode,
          ),
      ),
    [postcodeSearch, postcodesWithLocations.length, selectedPostcodes.length],
  )

  const locationsCount = useMemo(
    () =>
      selectedPostcodes.reduce((acc, item) => acc + item.locations.length, 0),
    [selectedPostcodes.length],
  )

  const isLimitExceeded = useMemo(
    () => whitelistedLocations.length == totalUniqueMissions,
    [whitelistedLocations.length, totalUniqueMissions],
  )

  const locationResults = useMemo(
    () =>
      locations.filter(
        location =>
          location &&
          location.name &&
          location.name.toLowerCase().includes(locationSearch.toLowerCase()) &&
          !whitelistedLocations.some(
            selected => selected && selected.id === location.id,
          ),
      ),
    [locations, locationSearch, whitelistedLocations],
  )

  const consideredLocationsCount = useMemo(() => {
    //Total locations in the Distribution Zone
    if (
      (!selectedPostcodes.length && !whitelistedLocations.length) ||
      (whitelistedLocations.length && !selectedPostcodes.length)
    ) {
      return postcodesWithLocations.reduce(
        (acc, item) => acc + item.locations.length,
        0,
      )
    }

    if (selectedPostcodes.length && !whitelistedLocations.length) {
      return selectedPostcodes.reduce(
        (acc, item) => acc + item.locations.length,
        0,
      )
    }

    if (selectedPostcodes.length && whitelistedLocations.length) {
      const locationsIds = []
      selectedPostcodes.forEach(item => {
        item.locations.forEach(location => {
          locationsIds.push(location.id)
        })
      })
      whitelistedLocations.forEach(location => {
        locationsIds.push(location.id)
      })

      return [...new Set(locationsIds)].length
    }
  }, [
    whitelistedLocations.length,
    selectedPostcodes.length,
    postcodesWithLocations.length,
  ])

  const handleLocationSearch = e => {
    const value = e.target.value
    setLocationSearch(value)
    setShowLocationResults(true)
  }

  const handleLocationInputFocus = () => {
    setShowLocationResults(true)
  }

  const handleLocationSelect = location => {
    if (!whitelistedLocations.some(selected => selected.id === location.id)) {
      setWhitelistedLocations([...whitelistedLocations, location])
    }
    setLocationSearch('')
    setShowLocationResults(false)
  }

  const handleRemoveLocation = location => {
    setWhitelistedLocations(
      whitelistedLocations.filter(loc => loc.id !== location.id),
    )
  }

  return (
    <div className={classes.pageContent}>
      <div className={classes.contentTitleMain}>Select Location</div>
      <div className={classes.contentDescription} style={styles.mb8}>
        Use this section to narrow down where missions will take place by
        selecting specific <strong>zip codes</strong> and{' '}
        <strong>whitelisted locations.</strong>
      </div>
      <div className={classes.contentTitle}>Zip Codes (optional)</div>
      <div className={classes.contentDescription} style={styles.mb8}>
        Add the ZIP codes within the city you want to include in the generated
        missions. AI will prioritize the best performing locations within the
        selected ZIP codes.
        <br />
        <strong>If skipped</strong>, all locations in the city will be
        considered by the AI.
      </div>
      <div style={styles.zipCodesContainer}>
        <TextField
          id="postcode-search"
          type={'text'}
          underlineShow={false}
          style={styles.field}
          inputStyle={styles.input}
          value={postcodeSearch}
          onChange={handlePostcodeSearch}
          onFocus={handlePostcodeInputFocus}
          placeholder="Search ZIP codes"
          disabled={!isDistributionZoneExists}
        />
        <div style={{ ...styles.searchIcon, top: '7px' }}>
          <SearchIcon style={styles.iconColor} />
        </div>
        {showPostcodeResults && (
          <Paper
            ref={postcodeSearchRef}
            style={styles.zipResults}
            elevation={3}
          >
            {loading ? (
              <div style={styles.placeholder}>Loading...</div>
            ) : filteredPostcodeResults.length ? (
              <List>
                {filteredPostcodeResults.map(item => {
                  const count = item.locations.length
                  return (
                    <ListItem
                      button
                      key={item.postcode}
                      onClick={() => handlePostcodeSelect(item)}
                      style={{
                        opacity: !count ? 0.5 : 1,
                        cursor: !count ? 'not-allowed' : 'pointer',
                        pointerEvents: !count ? 'none' : 'auto',
                      }}
                    >
                      <div style={styles.listItemContainer}>
                        <div style={styles.listItemMainText}>
                          {count
                            ? `${item.postcode} - ${count} Locations`
                            : `${item.postcode} - No Locations`}
                        </div>
                      </div>
                    </ListItem>
                  )
                })}
              </List>
            ) : (
              <div style={styles.placeholder}>No results found</div>
            )}
          </Paper>
        )}
        {!isDistributionZoneExists ? (
          <div style={styles.belowInputWrapper}>
            <div style={styles.noDistributionZoneText}>
              Distribution Zone required for Zip Codes. Please update it for the
              City.
            </div>
            <div style={styles.limit}>
              Locations in selected ZIPs: {locationsCount}
            </div>
          </div>
        ) : (
          <div style={styles.belowInputWrapper}>
            <div style={styles.chipContainer}>
              {selectedPostcodes.map(item => (
                <Chip
                  key={item.postcode}
                  label={item.postcode}
                  onDelete={() => handleRemovePostcode(item)}
                  style={styles.chip}
                  deleteIcon={<CloseIcon style={styles.chipCloseIcon} />}
                  clickable={false}
                />
              ))}
            </div>
            <div style={styles.limit}>
              Locations in selected ZIPs: {locationsCount}
            </div>
          </div>
        )}
      </div>
      <div className={classes.contentTitleMain}>
        Whitelisted Locations (optional)
      </div>
      <div style={styles.whitelistedLocationsContainer}>
        <div>
          <div className={classes.contentDescription}>
            You can whitelist locations from the city. <br />
            <strong>
              The whitelisted locations will be used at least once by the AI.
            </strong>
          </div>
          <div style={styles.typesOfLocations}>
            <div style={styles.locationType}>
              Unsheltered Locations: <UnshelteredIcon />
            </div>
            <div style={styles.locationType}>
              Sheltered Locations: <ShelteredIcon />
            </div>
            <div style={styles.locationType}>
              Events: <EventsIcon />
            </div>
          </div>
        </div>
        <div style={styles.payAttention}>
          <div className={classes.contentDescription}>
            <strong>Pay attention</strong> to whether locations are sheltered or
            not as
            <br /> unsheltered locations may not perform well in case of{' '}
            <strong>
              bad <br /> weather.
            </strong>
          </div>
        </div>
      </div>
      <div style={styles.whiteListLocationsInputWrapper}>
        <TextField
          id="location-search"
          type={'text'}
          underlineShow={false}
          style={styles.field}
          inputStyle={styles.input}
          value={locationSearch}
          onChange={handleLocationSearch}
          onFocus={handleLocationInputFocus}
          placeholder="Search locations"
          disabled={isLimitExceeded}
        />
        <div
          style={{
            ...styles.searchIcon,
            top: 7,
          }}
        >
          <SearchIcon style={styles.iconColor} />
        </div>
        {showLocationResults && (
          <Paper
            ref={locationSearchRef}
            style={styles.zipResults}
            elevation={3}
          >
            {loading ? (
              <div style={styles.placeholder}>Loading...</div>
            ) : locationResults.length > 0 ? (
              <List>
                {locationResults.map(location => {
                  const icons = getLocationIcons(location)

                  const label = (
                    <div style={styles.locationSelectItem}>
                      <div style={styles.locationLabel}>
                        {icons} {location.name}
                      </div>
                      <div>{location.postcode}</div>
                    </div>
                  )

                  return (
                    <ListItem
                      button
                      key={location.id}
                      onClick={() => handleLocationSelect(location)}
                    >
                      <ListItemText primary={label} />
                    </ListItem>
                  )
                })}
              </List>
            ) : (
              <div style={styles.placeholder}>No results found</div>
            )}
          </Paper>
        )}
        <div style={styles.belowInputWrapper}>
          <div style={styles.chipContainer}>
            {whitelistedLocations.map(location => {
              const icons = getLocationIcons(location)
              const label = (
                <div style={styles.locationLabel}>
                  {icons} {location.name}
                </div>
              )

              return (
                <Chip
                  key={location.id}
                  label={label}
                  onDelete={() => handleRemoveLocation(location)}
                  style={styles.chip}
                  deleteIcon={<CloseIcon style={{ width: 16, height: 16 }} />}
                  clickable={false}
                />
              )
            })}
          </div>
          <div
            style={{
              ...styles.limit,
              ...(isLimitExceeded ? styles.limitExceeded : {}),
            }}
          >
            <TooltipModal content="The number of locations should be equal to or less than the total number of missions" />
            Limit: {whitelistedLocations.length} / {totalUniqueMissions}
          </div>
        </div>
      </div>
      <div style={styles.footerWrapper}>
        <div style={styles.footerContent}>
          <div style={styles.footerItem}>
            Whitelisted Locations: {whitelistedLocations.length}
          </div>
          <div style={styles.footerItem}>
            Considered Locations: {consideredLocationsCount}
          </div>
          <div style={styles.footerItem}>
            Total Unique Missions: {totalUniqueMissions}
          </div>
        </div>
      </div>
    </div>
  )
}

export default StepLocation
